import DocumentManager from '@/components/DocumentManager';
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, RefreshCw, MessageCircle, FileText, ChevronDown } from "lucide-react";
import { api, adminToken, authHeaders, formatApiError } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { useContent } from "@/lib/content";

const inputCls = "w-full bg-white border border-sand-300 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none px-4 py-3 text-sm text-basalt-900 transition-colors";
const STATUSES = ["new", "in_review", "quoted", "closed"];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      adminToken.set(data.token);
      onLogin();
    } catch (err) {
      toast.error(formatApiError(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-basalt-950 flex items-center justify-center px-4 grain relative">
      <form onSubmit={submit} className="w-full max-w-sm bg-sand-50 p-8 sm:p-10" data-testid="admin-login-form">
        <a href="/" aria-label="Merugo Tradex home"><img className="admin-brand-logo" src="/brand/logo.png" alt="Merugo Tradex" width="150" height="150"/></a>
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-basalt-700/70 mb-8">Admin Access</p>
        <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-basalt-700 mb-2">Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className={`${inputCls} mb-4`} />
        <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-basalt-700 mb-2">Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className={`${inputCls} mb-6`} />
        <button type="submit" disabled={busy} data-testid="admin-login-button" className="w-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-60 text-sand-50 font-mono text-xs uppercase tracking-[0.2em] py-4 rounded-full transition-colors">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function RfqRow({ r, onStatus }) {
  const [open, setOpen] = useState(false);
  const wa = r.phone ? `https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${r.name}, regarding your sourcing requirement ${r.reference} with Merugo Tradex…`)}` : null;
  return (
    <div className="border border-sand-300 bg-white" data-testid={`rfq-row-${r.reference}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-x-6 gap-y-1 px-5 py-4 text-left hover:bg-sand-100 transition-colors">
        <span className="font-mono text-xs text-terracotta">{r.reference}</span>
        <span className="font-medium text-sm text-basalt-900">{r.name}</span>
        <span className="text-xs text-basalt-700/70">{r.product_category}{r.product_name ? ` — ${r.product_name}` : ""}</span>
        <span className="text-xs text-basalt-700/60 ml-auto hidden sm:inline">{new Date(r.created_at).toLocaleDateString()}</span>
        <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border ${r.status === "new" ? "border-terracotta text-terracotta" : "border-sand-400 text-basalt-700"}`}>{r.status.replace("_", " ")}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-sand-300 px-5 py-5 grid md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-1.5 text-basalt-800">
            <p><span className="text-basalt-700/60">Company:</span> {r.company || "—"} · <span className="text-basalt-700/60">Country:</span> {r.country || "—"}</p>
            <p><span className="text-basalt-700/60">Email:</span> <a className="link-underline" href={`mailto:${r.email}`}>{r.email}</a></p>
            <p><span className="text-basalt-700/60">Phone:</span> {r.phone || "—"}</p>
            <p><span className="text-basalt-700/60">Qty:</span> {r.quantity || "—"} · <span className="text-basalt-700/60">Target:</span> {r.target_price || "—"} · <span className="text-basalt-700/60">Delivery:</span> {r.delivery_date || "—"}</p>
            <p><span className="text-basalt-700/60">Destination:</span> {r.destination_country || "—"} {r.destination_port ? `/ ${r.destination_port}` : ""}</p>
            {r.description && <p className="pt-2 text-basalt-700/85">{r.description}</p>}
            {r.notes && <p className="text-basalt-700/70 italic">{r.notes}</p>}
          </div>
          <div className="space-y-4">
            {r.product_category === "Natural Stone" && r.stone && Object.values(r.stone).some(Boolean) && (
              <div className="bg-sand-100 border border-sand-300 p-4 font-mono text-xs space-y-1.5">
                <p className="text-terracotta tracking-[0.18em] uppercase mb-2">Stone Spec</p>
                {Object.entries(r.stone).filter(([, v]) => v).map(([k, v]) => (
                  <p key={k} className="flex justify-between gap-4"><span className="text-basalt-700/60 uppercase">{k.replace(/_/g, " ")}</span><span>{v}</span></p>
                ))}
              </div>
            )}
            {r.files?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {r.files.map((f, i) => (
                  <a key={i} href={`${process.env.REACT_APP_BACKEND_URL || ''}${f.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-sand-300 px-2.5 py-1.5 text-xs text-basalt-800 hover:border-terracotta">
                    <FileText size={12} className="text-terracotta" /> {f.original}
                  </a>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <select value={r.status} onChange={(e) => onStatus(r._id, e.target.value)} data-testid={`rfq-status-select-${r.reference}`} className={`${inputCls} !w-auto !py-2`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              {wa && (
                <a href={wa} target="_blank" rel="noreferrer" data-testid={`rfq-whatsapp-reply-${r.reference}`} className="inline-flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-mono uppercase tracking-wider px-4 py-2.5 rounded-full">
                  <MessageCircle size={13} /> Reply
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContentEditor() {
  const { refresh } = useContent();
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/content").then((r) => setValues(r.data || {})).catch(() => {}); }, []);
  const keys = [
    { key: "contact_email", label: "Contact Email", type: "text" },
    { key: "linkedin_url", label: "LinkedIn profile URL (optional)", type: "text" },
    { key: "instagram_url", label: "Instagram profile URL (optional)", type: "text" },
    { key: "announcement", label: "Announcement Bar Text (optional)", type: "text" },
    ...Object.keys(IMAGES).map((k) => ({ key: `img_${k}`, label: k.replace(/_/g, " "), type: "image" })),
  ];
  const save = async () => {
    setBusy(true);
    try {
      const updates = values;
      await api.put("/admin/content", { updates }, authHeaders());
      await refresh();
      toast.success("Site content updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6" data-testid="content-editor">
      <p className="text-sm text-basalt-700/80 max-w-2xl">
        Update contact details, social profiles and site images. Clear an image URL to restore its default; clear a social URL to hide that link. Use full https:// URLs for social profiles.
      </p>
      <div className="grid md:grid-cols-2 gap-5">
        {keys.map(({ key, label, type }) => (
          <div key={key} className="border border-sand-300 bg-white p-4">
            <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-basalt-700 mb-2">{label}</label>
            {type === "image" && (
              <div className="mb-2 h-24 bg-sand-200 overflow-hidden">
                <img src={values[key] || IMAGES[key.replace("img_", "")]?.url} alt={label} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
            <input value={values[key] || ""} onChange={(e) => setValues({ ...values, [key]: e.target.value })} placeholder={type === "image" ? "https://… (new image URL)" : ""} data-testid={`content-input-${key}`} className={inputCls} />
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="content-save-button" className="bg-terracotta hover:bg-terracotta-hover disabled:opacity-60 text-sand-50 font-mono text-xs uppercase tracking-[0.2em] px-10 py-4 rounded-full transition-colors">
        {busy ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!adminToken.get());
  const [tab, setTab] = useState("rfqs");
  const [rfqs, setRfqs] = useState(null);

  const load = () => {
    api.get("/admin/rfqs", authHeaders())
      .then((r) => setRfqs(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) { adminToken.clear(); setAuthed(false); }
        else toast.error(formatApiError(e));
      });
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  const onStatus = async (id, status) => {
    try {
      await api.patch(`/admin/rfqs/${id}`, { status }, authHeaders());
      setRfqs((prev) => prev.map((r) => (r._id === id ? { ...r, status } : r)));
      toast.success("Status updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-sand-100">
      <header className="bg-basalt-950 text-sand-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <p className="font-serif text-lg">MERUGO TRADEX <span className="font-mono text-[10px] tracking-[0.3em] text-sand-400 ml-2">ADMIN</span></p>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="w-9 h-9 flex items-center justify-center border border-basalt-700 rounded-full hover:border-sand-400"><RefreshCw size={14} /></button>
            <button aria-label="Sign out" onClick={async () => { try { await api.post('/auth/logout'); adminToken.clear(); setAuthed(false); } catch { toast.error('Could not sign out. Please try again.'); } }} data-testid="admin-logout-button" className="w-9 h-9 flex items-center justify-center border border-basalt-700 rounded-full hover:border-sand-400"><LogOut size={14} /></button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
        <div className="flex gap-2 mb-8">
          {[["rfqs", "RFQ Submissions"], ["content", "Site Content"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`font-mono text-[11px] uppercase tracking-[0.16em] px-5 py-3 rounded-full border transition-colors ${tab === k ? "bg-basalt-950 text-sand-50 border-basalt-950" : "border-sand-300 text-basalt-800 hover:border-basalt-800"}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "rfqs" ? (
          rfqs === null ? (
            <p className="text-basalt-700/70 text-sm">Loading…</p>
          ) : rfqs.length === 0 ? (
            <div className="border border-dashed border-sand-400 bg-white p-14 text-center" data-testid="rfq-empty-state">
              <p className="font-serif text-2xl text-basalt-900 mb-2">No requirements yet.</p>
              <p className="text-sm text-basalt-700/70">New RFQ submissions from the website will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="rfq-list">
              {rfqs.map((r) => <RfqRow key={r._id} r={r} onStatus={onStatus} />)}
            </div>
          )
        ) : (
          <><ContentEditor /><DocumentManager /></>
        )}
      </div>
    </div>
  );
}
